import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, ShieldStar, ShieldCheck, Users as UsersIcon, FloppyDisk, ArrowCounterClockwise,
  Sparkle, Calendar, ChatCircleDots, ShareNetwork, Gear, Factory, ClipboardText, Kanban,
  BookOpen, Barcode, ShoppingBag, Wrench, Storefront, Package, CaretDown, CaretRight,
  MagnifyingGlass, Warning, LockSimple, ArrowsOutSimple, ArrowsInSimple,
  UserCircle, Check, X, Eye,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXPAND_STORAGE_KEY = 'sb.roles.expandedApps';

const loadExpandedMap = () => {
  try {
    const raw = localStorage.getItem(EXPAND_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
};

const APP_ICONS = {
  kintai:        { Icon: Calendar,       color: 'bg-blue-100 text-blue-600' },
  chat:          { Icon: ChatCircleDots, color: 'bg-emerald-100 text-emerald-600' },
  it:            { Icon: ShareNetwork,   color: 'bg-violet-100 text-violet-600' },
  mfg:           { Icon: Factory,        color: 'bg-orange-100 text-orange-600' },
  shizai:        { Icon: ClipboardText,  color: 'bg-rose-100 text-rose-600' },
  parts_request: { Icon: ShoppingBag,    color: 'bg-orange-100 text-orange-600' },
  planner:       { Icon: Kanban,         color: 'bg-indigo-100 text-indigo-600' },
  knowledge:     { Icon: BookOpen,       color: 'bg-teal-100 text-teal-600' },
  engineering:   { Icon: Wrench,         color: 'bg-teal-100 text-teal-700' },
  scanner:       { Icon: Barcode,        color: 'bg-cyan-100 text-cyan-600' },
  marketing:     { Icon: Storefront,     color: 'bg-pink-100 text-pink-600' },
  bihin:         { Icon: Package,        color: 'bg-amber-100 text-amber-600' },
  settings:      { Icon: Gear,           color: 'bg-zinc-100 text-zinc-600' },
};

const ACTION_LABEL = {
  view: '閲覧', create: '作成', edit: '編集', delete: '削除', approve: '承認', verify: '確認',
  publish: '公開', release: '払出', operate: '操作', move: '移動', manage: '管理',
  configure: '設定', request: '申請', backup: 'バックアップ',
};
const ACTION_STYLE = {
  delete: 'bg-rose-50 text-rose-600 border-rose-200',
  approve: 'bg-amber-50 text-amber-700 border-amber-200',
  publish: 'bg-amber-50 text-amber-700 border-amber-200',
  backup: 'bg-amber-50 text-amber-700 border-amber-200',
};

const ROLE_META = {
  admin:   { label: 'admin',     short: 'Admin',   Icon: ShieldStar,  badge: 'bg-violet-50 text-violet-700 border-violet-200', accent: 'border-t-violet-500', iconBg: 'from-violet-100 to-violet-200 text-violet-700' },
  manager: { label: '管理者',    short: 'Manager', Icon: ShieldCheck, badge: 'bg-amber-50 text-amber-700 border-amber-200',    accent: 'border-t-amber-500',  iconBg: 'from-amber-100 to-amber-200 text-amber-700' },
  user:    { label: 'ユーザー',  short: 'User',    Icon: UsersIcon,   badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',      accent: 'border-t-zinc-400',   iconBg: 'from-zinc-100 to-zinc-200 text-zinc-700' },
};

const BASELINE_ITEMS = ['設定エリアの閲覧', '自分の署名・捺印の登録', '部署管理の閲覧', '自分の申請への署名'];

const appPerms = (app) => (app.groups || []).flatMap((g) => g.perms || []);

const SOURCE_BADGE = {
  override: { label: '個別上書き', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  app_off:  { label: 'アプリOFF', cls: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
  membership: { label: 'メンバー制', cls: 'bg-violet-50 text-violet-600 border-violet-200' },
};

function TriState({ value, onChange, disabled, testid }) {
  // value: true(許可) / false(拒否) / undefined(継承)
  const opts = [
    { v: null, label: '継承', on: 'bg-zinc-500 text-white' },
    { v: true, label: 'ON', on: 'bg-emerald-500 text-white' },
    { v: false, label: 'OFF', on: 'bg-rose-500 text-white' },
  ];
  return (
    <span className={`inline-flex rounded border border-zinc-200 overflow-hidden shrink-0 ${disabled ? 'opacity-40' : ''}`} data-testid={testid}>
      {opts.map((o) => {
        const active = (o.v === null && value === undefined) || o.v === value;
        return (
          <button key={String(o.v)} type="button" disabled={disabled}
            onClick={() => !active && onChange(o.v)}
            data-testid={`${testid}-${o.v === null ? 'inherit' : o.v ? 'on' : 'off'}`}
            className={`px-1.5 py-0.5 text-[9px] font-bold transition-colors ${active ? o.on : 'bg-white text-zinc-400 hover:bg-zinc-50'}`}>
            {o.label}
          </button>
        );
      })}
    </span>
  );
}

function PermissionPreview({ registry, requestUid }) {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState('');
  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.users || []);
      setUsers(list.filter((u) => u.is_active !== false && u.active !== false));
    }).catch(() => {});
  }, []);

  // オーバーライド一覧の「プレビューで開く」からの遷移
  useEffect(() => {
    if (requestUid && requestUid !== sel) load(requestUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestUid]);

  const load = (uid) => {
    setSel(uid);
    setEditMode(false);
    axios.get(`${API}/role-capabilities/preview/${uid}`)
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'プレビューの取得に失敗しました'); });
  };

  const saveOverride = async (key, value) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/role-capabilities/preview/${sel}/overrides`,
        { overrides: { [key]: value } });
      setData(r.data);
      toast.success(value === null ? '上書きを解除しました（ロールから継承）' : value ? '許可の上書きを保存しました' : '拒否の上書きを保存しました');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const eff = data?.effective || {};
  const sources = data?.sources || {};
  const overrides = data?.overrides || {};
  const roleMeta = data ? (ROLE_META[data.user.role] || ROLE_META.user) : null;
  const ovVal = (k) => (k in overrides ? overrides[k] : undefined);
  const membershipKey = (k) => k === 'app_it' || k === 'it_edit';

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="permission-preview-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-sky-700 shrink-0">
          <Eye size={18} weight="duotone" />
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-zinc-900">権限プレビュー</h3>
          <p className="text-[11px] text-zinc-500">ユーザーを選ぶと、その人が「今」利用できるアプリと操作（ロール＋個別上書き＋メンバー制＋アプリOFFの合成結果）を表示します（読み取り専用）</p>
        </div>
        <div className="ml-auto w-full sm:w-72">
          <Select value={sel} onValueChange={load}>
            <SelectTrigger className="h-9" data-testid="preview-user-select">
              <SelectValue placeholder="ユーザーを選択…" />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name || u.email}（{ROLE_META[u.role]?.label || u.role}）</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <div className="px-5 py-4 space-y-4" data-testid="preview-panel">
          <div className="flex flex-wrap items-center gap-2">
            <UserCircle size={18} className="text-zinc-400" />
            <span className="text-sm font-semibold text-zinc-800" data-testid="preview-user-name">{data.user.name || data.user.email}</span>
            <span className="text-[11px] text-zinc-400">{data.user.email}</span>
            {roleMeta && (
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${roleMeta.badge}`}>{roleMeta.label}</span>
            )}
            {data.memberships?.it && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-violet-50 text-violet-600 border-violet-200 font-semibold">ITメンバー</span>}
            {data.memberships?.engineering && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-teal-50 text-teal-600 border-teal-200 font-semibold">技術メンバー</span>}
            {data.override_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200 font-semibold">個別上書き {data.override_count}件</span>}
            <Button size="sm" variant={editMode ? 'default' : 'outline'} onClick={() => setEditMode((v) => !v)}
              data-testid="preview-edit-toggle"
              className={`ml-auto h-7 text-xs ${editMode ? 'bg-teal-700 hover:bg-teal-800' : ''}`}>
              {editMode ? '編集を終了' : '上書きを編集'}
            </Button>
          </div>
          {editMode && (
            <p className="text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-3 py-2" data-testid="preview-edit-hint">
              このユーザーだけの個別上書きを設定します（即時保存）。「継承」＝ロールの設定に従う、「ON/OFF」＝ロール設定を上書き。最後のFULL管理者をロックアウトする変更は拒否されます。
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {registry.map((app) => {
              const meta = APP_ICONS[app.id] || APP_ICONS.settings;
              const appOn = eff[app.app] !== false;
              const perms = appPerms(app);
              return (
                <div key={app.id} className={`rounded-lg border p-3 ${appOn ? 'border-zinc-200 bg-white' : 'border-zinc-100 bg-zinc-50/60'}`} data-testid={`preview-app-${app.id}`}>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${meta.color} shrink-0`}>
                      <meta.Icon size={13} weight="duotone" />
                    </span>
                    <span className={`text-[13px] font-semibold ${appOn ? 'text-zinc-800' : 'text-zinc-400'}`}>{app.label}</span>
                    <span className="ml-auto flex items-center gap-1.5">
                      {editMode && !app.hide_switch && (
                        <TriState value={ovVal(app.app)} disabled={saving || membershipKey(app.app)}
                          onChange={(v) => saveOverride(app.app, v)} testid={`ov-app-${app.id}`} />
                      )}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${appOn ? 'bg-emerald-50 text-emerald-600' : 'bg-zinc-100 text-zinc-400'}`} data-testid={`preview-app-state-${app.id}`}>
                        {appOn ? '利用可' : 'OFF'}
                      </span>
                    </span>
                  </div>
                  {perms.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {perms.map((p) => {
                        const on = !!eff[p.key];
                        const src = SOURCE_BADGE[sources[p.key]];
                        return (
                          <li key={p.key} className="flex items-center gap-1.5 text-[12px]" data-testid={`preview-perm-${p.key}`}>
                            {on
                              ? <Check size={12} weight="bold" className="text-emerald-500 shrink-0" />
                              : <X size={12} weight="bold" className="text-zinc-300 shrink-0" />}
                            <span className={on ? 'text-zinc-700' : 'text-zinc-400'}>{p.label}</span>
                            {src && <span className={`text-[9px] px-1 py-0 rounded border font-semibold ${src.cls}`}>{src.label}</span>}
                            {editMode && (
                              <span className="ml-auto">
                                <TriState value={ovVal(p.key)} disabled={saving || !!p.admin_only || membershipKey(p.key)}
                                  onChange={(v) => saveOverride(p.key, v)} testid={`ov-${p.key}`} />
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {app.membership && (
                    <p className="mt-2 text-[10px] text-zinc-400 leading-snug">
                      {app.id === 'engineering'
                        ? (data.memberships?.engineering ? '技術メンバー: 操作可（上記権限に従う）' : '技術メンバーではないため操作不可（閲覧のみ）')
                        : (data.memberships?.it ? 'ITメンバー: 利用・編集可' : 'ITメンバーではないため利用不可')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PermRow({ role, perm, caps, disabled, onToggle }) {
  const actionCls = ACTION_STYLE[perm.action] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
  const adminOnly = !!perm.admin_only;
  return (
    <div className={`flex items-center justify-between gap-3 py-1.5 ${disabled || adminOnly ? '' : ''}`} data-testid={`cap-row-${role}-${perm.key}`}>
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-zinc-800 flex flex-wrap items-center gap-1.5">
          {perm.label}
          <span className={`text-[9px] px-1 py-0 rounded border font-semibold ${actionCls}`}>{ACTION_LABEL[perm.action] || perm.action}</span>
          {perm.risk === 'high' && (
            <span className="text-[9px] px-1 py-0 rounded border bg-rose-50 text-rose-600 border-rose-200 font-semibold inline-flex items-center gap-0.5">
              <Warning size={9} weight="fill" /> 高リスク
            </span>
          )}
          {adminOnly && (
            <span className="text-[9px] px-1 py-0 rounded border bg-zinc-100 text-zinc-500 border-zinc-200 font-semibold inline-flex items-center gap-0.5">
              <LockSimple size={9} weight="fill" /> admin固定
            </span>
          )}
        </p>
        {perm.desc && <p className="text-[11px] text-zinc-500 leading-snug">{perm.desc}</p>}
      </div>
      <Switch
        checked={!!caps[perm.key]}
        disabled={disabled || adminOnly}
        onCheckedChange={(v) => !disabled && !adminOnly && onToggle(perm.key, v)}
        data-testid={`cap-switch-${role}-${perm.key}`}
        className="shrink-0"
      />
    </div>
  );
}

function AppSection({ role, app, caps, locked, onToggle, expanded, setExpanded, query }) {
  const meta = APP_ICONS[app.id] || APP_ICONS.settings;
  const appOn = caps[app.app] !== false;
  const perms = useMemo(() => appPerms(app), [app]);
  const q = (query || '').trim();
  const matched = q
    ? perms.filter((p) => `${p.label}${p.desc || ''}${app.label}`.includes(q))
    : perms;
  const enabledCount = useMemo(() => perms.filter((p) => !!caps[p.key]).length, [perms, caps]);
  if (q && matched.length === 0 && !app.label.includes(q)) return null;
  const shownPerms = q && matched.length > 0 && !app.label.includes(q) ? matched : perms;
  const isOpen = q ? true : expanded;
  const membership = app.membership;
  const bodyId = `app-body-${role}-${app.id}`;

  return (
    <div className="px-5 py-3" data-testid={`app-block-${role}-${app.id}`}>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold text-zinc-900 min-w-0 text-left"
          data-testid={`app-header-${role}-${app.id}`}
        >
          {(perms.length > 0 || app.note) ? (
            isOpen ? <CaretDown size={12} weight="bold" className="text-zinc-400 shrink-0" /> : <CaretRight size={12} weight="bold" className="text-zinc-400 shrink-0" />
          ) : <span className="w-3 shrink-0" />}
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${meta.color} shrink-0`}>
            <meta.Icon size={15} weight="duotone" />
          </span>
          <span className="leading-snug">{app.label}</span>
          {membership && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border bg-violet-50 text-violet-600 border-violet-200 font-semibold shrink-0">メンバー制</span>
          )}
          {perms.length > 0 && (
            <span className={`text-[10px] shrink-0 ${enabledCount > 0 ? 'text-zinc-500' : 'text-zinc-300'}`} data-testid={`app-summary-${role}-${app.id}`}>
              {enabledCount} / {perms.length} 有効
            </span>
          )}
        </button>
        {app.hide_switch ? (
          <span className="text-[10px] text-zinc-400 shrink-0">メンバー一覧で管理</span>
        ) : (
          <Switch
            checked={appOn}
            disabled={locked}
            onCheckedChange={(v) => !locked && onToggle(app.app, v)}
            data-testid={`app-switch-${role}-${app.id}`}
          />
        )}
      </div>

      {isOpen && (
        <div className={`mt-2 ml-9 pl-3 border-l-2 border-zinc-100 ${appOn || app.hide_switch ? '' : 'opacity-40'}`}>
          {app.note && <p className="text-[11px] text-zinc-400 leading-snug py-1">{app.note}</p>}
          {(app.groups || []).map((g) => {
            const gPerms = (g.perms || []).filter((p) => shownPerms.includes(p));
            if (gPerms.length === 0) return null;
            return (
              <div key={g.label} className="py-1" data-testid={`group-${role}-${app.id}-${g.label}`}>
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{g.label}</p>
                {gPerms.map((perm) => (
                  <PermRow key={perm.key} role={role} perm={perm} caps={caps}
                    disabled={locked || !appOn} onToggle={onToggle} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OverrideOverview({ registry, onInspect }) {
  const [users, setUsers] = useState(null);

  useEffect(() => {
    axios.get(`${API}/role-capabilities/overrides`)
      .then((r) => setUsers(r.data.users || []))
      .catch(() => setUsers([]));
  }, []);

  const labelMap = useMemo(() => {
    const m = {};
    registry.forEach((a) => {
      m[a.app] = { app: a.label, label: 'アプリ利用' };
      (a.groups || []).forEach((g) => (g.perms || []).forEach((p) => { m[p.key] = { app: a.label, label: p.label }; }));
    });
    return m;
  }, [registry]);

  if (!users) return null;

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="override-overview-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-amber-700 shrink-0">
          <Warning size={18} weight="duotone" />
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-zinc-900">個別オーバーライド一覧</h3>
          <p className="text-[11px] text-zinc-500">ロール既定と異なる個別権限を持つユーザーの一覧 — 特別扱いをひと目で把握できます</p>
        </div>
        <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-200" data-testid="override-user-count">
          {users.length} 名
        </span>
      </div>
      {users.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-zinc-400" data-testid="override-empty">
          個別上書きを持つユーザーはいません — 全員がロール既定どおりです。
        </p>
      ) : (
        <div className="divide-y divide-zinc-200">
          {users.map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.user;
            return (
              <div key={u.id} className="px-5 py-3 flex flex-wrap items-start gap-2" data-testid={`override-row-${u.id}`}>
                <div className="flex items-center gap-2 min-w-[200px]">
                  <UserCircle size={16} className="text-zinc-400 shrink-0" />
                  <span className="text-[13px] font-semibold text-zinc-800">{u.name || u.email}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${meta.badge}`}>{meta.label}</span>
                  {u.active === false && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-zinc-100 text-zinc-500 border-zinc-200 font-semibold">無効</span>}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-[220px]">
                  {Object.entries(u.overrides).map(([k, o]) => {
                    const info = labelMap[k] || { app: '', label: k };
                    return (
                      <span key={k}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${o.value ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-600 border-rose-200'}`}
                        title={`ロール既定: ${o.role_default ? '許可' : '拒否'} → 上書き: ${o.value ? '許可' : '拒否'}`}
                        data-testid={`override-chip-${u.id}-${k}`}>
                        {o.value ? <Check size={10} weight="bold" /> : <X size={10} weight="bold" />}
                        {info.app ? `${info.app}: ` : ''}{info.label}
                      </span>
                    );
                  })}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs ml-auto shrink-0"
                  onClick={() => onInspect(u.id)} data-testid={`override-inspect-${u.id}`}>
                  <Eye size={12} className="mr-1" /> プレビューで開く
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RoleCard({ role, registry, caps, dirtyCaps, locked, onToggle, onReset, onSave, saving, expandedMap, setExpandedFor, query }) {
  const meta = ROLE_META[role];
  const Icon = meta.Icon;
  const isDirty = Object.keys(dirtyCaps).length > 0;
  const enabledApps = registry.filter((a) => a.membership || caps[a.app] !== false).length;

  return (
    <div className={`bg-white rounded-xl border border-zinc-200 border-t-4 ${meta.accent} shadow-sm overflow-hidden flex flex-col`} data-testid={`role-card-${role}`}>
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${meta.iconBg} shrink-0`}>
            <Icon size={18} weight="duotone" />
          </span>
          <div>
            <h3 className="font-bold text-zinc-900 flex items-center gap-2">
              {meta.label}
              {locked && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 font-semibold uppercase tracking-wider inline-flex items-center gap-1" data-testid={`role-locked-${role}`}>
                  <LockSimple size={9} weight="fill" /> ロック
                </span>
              )}
            </h3>
            <p className="text-[11px] text-zinc-500">
              {locked ? '全権限が常に付与（保護されたロール）' : `${enabledApps} / ${registry.length} 個のアプリが利用可能`}
            </p>
          </div>
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.badge}`}>
          {meta.short}
        </span>
      </div>

      <div className="divide-y divide-zinc-200 flex-1">
        {registry.map((app) => (
          <AppSection
            key={app.id} role={role} app={app} caps={caps} locked={locked}
            onToggle={onToggle}
            expanded={!!expandedMap[`${role}:${app.id}`]}
            setExpanded={(v) => setExpandedFor(role, app.id, v)}
            query={query}
          />
        ))}
      </div>

      {!locked && (
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/40 flex items-center justify-between gap-2 sticky bottom-0">
          <p className={`text-[11px] ${isDirty ? 'text-amber-700 font-semibold' : 'text-zinc-400'}`} data-testid={`dirty-state-${role}`}>
            {isDirty ? `${Object.keys(dirtyCaps).length} 件の未保存の変更` : '変更なし'}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => onReset(role)} disabled={!isDirty || saving}
              data-testid={`btn-reset-${role}`} className="h-8">
              <ArrowCounterClockwise size={13} className="mr-1" /> 元に戻す
            </Button>
            <Button size="sm" onClick={() => onSave(role)} disabled={!isDirty || saving}
              data-testid={`btn-save-${role}`} className="h-8 bg-teal-700 hover:bg-teal-800">
              <FloppyDisk size={13} className="mr-1" /> 保存
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RolesSettings() {
  const { refreshMe } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ keys: [], order: [], locked: [] });
  const [registry, setRegistry] = useState([]);
  const [previewReq, setPreviewReq] = useState('');
  const [original, setOriginal] = useState({});
  const [draft, setDraft] = useState({});
  const [expandedMap, setExpandedMap] = useState(loadExpandedMap);
  const [query, setQuery] = useState('');

  useEffect(() => {
    try { localStorage.setItem(EXPAND_STORAGE_KEY, JSON.stringify(expandedMap)); } catch (_e) { /* noop */ }
  }, [expandedMap]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/role-capabilities`);
      const data = r.data || {};
      setMeta({ keys: data.keys || [], order: data.order || ['admin', 'manager', 'user'], locked: data.locked || ['admin'] });
      setRegistry(data.registry || []);
      setOriginal(data.roles || {});
      setDraft(JSON.parse(JSON.stringify(data.roles || {})));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (role, key, value) => {
    setDraft((d) => ({ ...d, [role]: { ...d[role], [key]: value } }));
  };

  const dirtyFor = (role) => {
    const o = original[role] || {};
    const d = draft[role] || {};
    const out = {};
    meta.keys.forEach((k) => { if (!!o[k] !== !!d[k]) out[k] = d[k]; });
    return out;
  };

  const reset = (role) => setDraft((d) => ({ ...d, [role]: { ...(original[role] || {}) } }));

  const save = async (role) => {
    const updates = dirtyFor(role);
    if (Object.keys(updates).length === 0) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/role-capabilities/${role}`, { capabilities: updates });
      const newCaps = r.data?.capabilities || {};
      setOriginal((o) => ({ ...o, [role]: newCaps }));
      setDraft((d) => ({ ...d, [role]: newCaps }));
      toast.success(`${ROLE_META[role]?.label || role} の権限を保存しました`);
      try { await refreshMe(); } catch (_e) { /* noop */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const setExpandedFor = (role, appId, v) => setExpandedMap((m) => ({ ...m, [`${role}:${appId}`]: v }));
  const setAll = (v) => {
    const m = {};
    (meta.order || []).forEach((role) => registry.forEach((a) => { m[`${role}:${a.id}`] = v; }));
    setExpandedMap(m);
  };

  const orderedRoles = useMemo(() => meta.order || [], [meta.order]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-zinc-200 px-5 py-16 text-center">
        <div className="w-8 h-8 mx-auto rounded-full border-4 border-zinc-200 border-t-[#0052FF] animate-spin" />
        <p className="mt-3 text-sm text-zinc-500">読み込み中…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-settings-page">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">設定</p>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
          <Shield size={26} weight="duotone" className="text-teal-700" />
          ロール権限
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-1 leading-relaxed">
          アプリ → 機能 → 操作の3階層で権限を設定します。削除・承認・公開などの高リスク操作は編集権限から分離されています。adminロールはロックされ、常に全権限が付与されます。
        </p>
      </header>

      <div className="bg-white border border-blue-200 rounded-xl p-5 flex gap-4 items-start shadow-sm" data-testid="roles-baseline-card">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-blue-200 text-teal-700 shrink-0">
          <Sparkle size={20} weight="duotone" />
        </span>
        <div>
          <p className="text-sm font-bold text-zinc-900">全ロール共通（基本機能）</p>
          <p className="text-[13px] text-zinc-600 mt-1 leading-relaxed">
            ログインした全員が利用できます：{BASELINE_ITEMS.join('　・　')}
          </p>
          <p className="text-[12px] text-zinc-500 mt-1.5">
            アプリを OFF にすると、そのアプリ内の権限はバックエンドでもまとめて拒否されます（子設定は保持され、再度ONにすると戻ります）。
          </p>
        </div>
      </div>

      <PermissionPreview registry={registry} requestUid={previewReq} />

      <OverrideOverview registry={registry}
        onInspect={(uid) => { setPreviewReq(uid); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="権限を検索（例: 削除、承認、ラベル）…"
            className="pl-8 h-9" data-testid="perm-search-input" />
        </div>
        <Button size="sm" variant="outline" onClick={() => setAll(true)} className="h-9" data-testid="btn-expand-all">
          <ArrowsOutSimple size={13} className="mr-1" /> すべて展開
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAll(false)} className="h-9" data-testid="btn-collapse-all">
          <ArrowsInSimple size={13} className="mr-1" /> すべて折りたたむ
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 items-start">
        {orderedRoles.map((role) => (
          <RoleCard
            key={role} role={role} registry={registry}
            caps={draft[role] || {}} dirtyCaps={dirtyFor(role)}
            locked={meta.locked.includes(role)}
            onToggle={(k, v) => toggle(role, k, v)}
            onReset={reset} onSave={save} saving={saving}
            expandedMap={expandedMap} setExpandedFor={setExpandedFor} query={query}
          />
        ))}
      </div>

      <div className="rounded-md bg-blue-50/70 border border-blue-200 px-4 py-3 text-[12px] text-blue-900 leading-relaxed">
        変更は即座に反映されます — 該当ロールのユーザーが次のページ遷移またはリロードした時点で新しい権限が適用されます。保存は各ロールカードごとに、変更したキーのみが送信されます。
      </div>
    </div>
  );
}
